import React, { useState } from "react";
import { createPortal } from "react-dom";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";
import { apiPost } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";
import { toast } from "wc-toast";

/* ───────────────────────────────────────────────────────────────────────────
 * ChangePasswordModal — reference kingexch111 "Change Password" popup.
 * No routing: opens in place over the current page. On success it just closes.
 * Scoped `cpm-*` class names so it never clashes with theme CSS.
 * ─────────────────────────────────────────────────────────────────────────── */

const Field = ({ label, value, onChange, show, toggle, error }) => (
  <div style={{ marginBottom: "14px" }}>
    <label style={{ display: "block", fontSize: "14px", color: "#111", marginBottom: "6px" }}>
      {label} <span style={{ color: "#e53935" }}>*</span>
    </label>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        border: `1px solid ${error ? "#e53935" : "#d5d5d5"}`,
        borderRadius: "4px",
        background: "#fff",
        padding: "0 12px",
        height: "42px",
      }}
    >
      <input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        style={{
          flex: 1,
          minWidth: 0,
          border: "none",
          outline: "none",
          background: "transparent",
          boxShadow: "none",
          borderRadius: 0,
          padding: 0,
          margin: 0,
          position: "static",
          appearance: "none",
          WebkitAppearance: "none",
          fontSize: "14px",
          color: "#111",
        }}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label="toggle password"
        style={{ border: "none", background: "transparent", cursor: "pointer", color: "#555", display: "flex" }}
      >
        {show ? <AiFillEye size={20} /> : <AiFillEyeInvisible size={20} />}
      </button>
    </div>
    {error && <div style={{ color: "#e53935", fontSize: "12px", marginTop: "4px" }}>{error}</div>}
  </div>
);

const ChangePasswordModal = ({ open, onClose }) => {
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const reset = () => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
    setErrors({});
  };

  const close = () => {
    reset();
    onClose?.();
  };

  const validate = () => {
    const e = {};
    if (!oldPassword) e.oldPassword = "Please enter current password";
    if (!newPassword) e.newPassword = "Please enter new password";
    if (!confirmPassword) e.confirmPassword = "Please confirm password";
    else if (newPassword && confirmPassword !== newPassword) e.confirmPassword = "Password does not match";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onConfirm = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      const { status, data } = await apiPost(apiPath.changePassword, {
        oldPassword,
        newPassword,
      });
      if (status === 200 && data?.success) {
        toast.success(data?.message || "Password changed successfully");
        localStorage.setItem("pass_changed", "true");
        close();
      } else {
        toast.error(data?.message || "Something went wrong");
      }
    } catch (err) {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="cpm-overlay"
      onMouseDown={(e) => e.target === e.currentTarget && close()}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 10000000,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        padding: "10px 0 0",
      }}
    >
      <div
        className="cpm-box"
        style={{
          width: "100%",
          maxWidth: "375px",
          background: "#fff",
          borderRadius: "4px",
          overflow: "hidden",
          boxShadow: "0 10px 40px rgba(0,0,0,0.35)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "linear-gradient(180deg, #33475b 0%, #243746 100%)",
            color: "#fff",
            padding: "6px 14px",
          }}
        >
          <span style={{ fontSize: "15px", fontWeight: 700 }}>Change Password</span>
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            style={{ border: "none", background: "transparent", color: "#FFFFFF", cursor: "pointer", fontSize: "21px", fontFamily: "Tahoma, Helvetica, sans-serif", lineHeight: 1, padding: 0 }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: "16px 16px 6px" }}>
          <Field
            label="Old Password"
            value={oldPassword}
            onChange={setOldPassword}
            show={showOld}
            toggle={() => setShowOld((v) => !v)}
            error={errors.oldPassword}
          />
          <Field
            label="New Password"
            value={newPassword}
            onChange={setNewPassword}
            show={showNew}
            toggle={() => setShowNew((v) => !v)}
            error={errors.newPassword}
          />
          <Field
            label="Confirm Password"
            value={confirmPassword}
            onChange={setConfirmPassword}
            show={showConfirm}
            toggle={() => setShowConfirm((v) => !v)}
            error={errors.confirmPassword}
          />
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "12px",
            padding: "12px 16px 14px",
            borderTop: "1px solid #eee",
          }}
        >
          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            style={{
              background: "#5c6670",
              color: "#fff",
              border: "none",
              borderRadius: "3px",
              padding: "8px 18px",
              fontSize: "14px",
              cursor: loading ? "default" : "pointer",
              opacity: loading ? 0.7 : 1,
            }}
          >
            {loading ? "..." : "Confirm"}
          </button>
          <button
            type="button"
            onClick={close}
            style={{
              background: "#aeb6bd",
              color: "#1b2d38",
              border: "none",
              borderRadius: "3px",
              padding: "8px 18px",
              fontSize: "14px",
              cursor: "pointer",
            }}
          >
            No
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ChangePasswordModal;
